import React, { useRef, useEffect, useCallback } from 'react';
import Moveable from 'react-moveable';
import { Frame } from 'scenejs';

const HeightResizableDiv = ({ height, onResizeStop, className, containerRef, dependentRef, resizeCallback, handle = ['n', 's'] }) => {
  const moveableRef = useRef(null);
  const frameMap = useRef(new Map());
  const heightRef = useRef(null);

  useEffect(() => {
    moveableRef.current.updateRect();
    frameMap.current.clear();
  }, [height]);

  const newFrame = (el, save = true) => {
    const height = `${el.offsetHeight}px`;
    const actualHeightStyle = el.style.height;
    const frame = new Frame({
      height: save ? height : actualHeightStyle, // To retain the relative height in Calc instead of fixed pixels
    });

    if (save) {
      frameMap.current.set(el, frame);
    }
    return frame;
  };

  const getFrame = useCallback((target) => {
    return frameMap.current.get(target) || newFrame(target);
  }, [])

  const handleRender = useCallback(({ target }, isGroup = false) => {
    target.style.cssText += getFrame(target).toCSS();
    if (dependentRef) {
      dependentRef.current.style.cssText += getFrame(dependentRef.current).toCSS();
    }
  }, []);

  const handleMoveableResize = useCallback(({ target, height, delta }) => {
    const frame = getFrame(target);
    frame.set('height', `${height.toFixed(3)}px`);

    if (dependentRef) {
      const dependentFrame = getFrame(dependentRef.current);
      const dependentFrameHeight = parseFloat(dependentFrame.get('height').replaceAll('px', ''));
      dependentFrame.set('height', `${dependentFrameHeight - delta[1]}px`);
    }
  }, []);

  const updateDimensions = useCallback(({ target }) => {
    const frame = getFrame(target);
    const newHeight = parseFloat(frame.get('height').replaceAll('px', ''));
    const resizeSuccess = onResizeStop({
      height: newHeight
    });
    if (!resizeSuccess) {
      // Manually performing handle render
      target.style.cssText += heightRef.current.staticEl.toCSS();
      if (dependentRef) {
        dependentRef.current.style.cssText += heightRef.current.dependentEl.toCSS();
      }
    } else {
      const previousHeight = heightRef.current.staticEl.get('height').replaceAll('px', '');
      if (resizeCallback && previousHeight > newHeight) { // More space added
        resizeCallback();
      }
    }
    // Removing all values from map to start afresh
    frameMap.current.clear();
  }, [resizeCallback]);

  const setInitialValue = useCallback(({ target }) => {
    const frame = newFrame(target, false);
    const dependentFrame = dependentRef ? newFrame(dependentRef.current, false) : null;

    heightRef.current = {
      staticEl: frame,
      dependentEl: dependentFrame
    };
  }, []);

  return (
    <>
    <Moveable
        target={containerRef}
        ref={moveableRef}
        className={className}
        padding={{ left: 0, top: 0, right: 0, bottom: 0 }}
        container={null}
        renderDirections={handle}
        origin={false}
        checkInput={false}
        keepRatio={true}
        draggable={false}
        resizable={true}
        rotatable={false}
        snappable={false}
        throttleResize={1}
        onRender={handleRender}
        onResizeStart={setInitialValue}
        onResize={handleMoveableResize}
        onResizeEnd={updateDimensions}
      />
    </>
  )
}

export default HeightResizableDiv;